import { useCallback, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";

export interface AdminAuthState {
  loading: boolean;
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  error: string | null;
}

export function useAdminAuth() {
  const [state, setState] = useState<AdminAuthState>({
    loading: true,
    user: null,
    session: null,
    isAdmin: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data: sessRes } = await supabase.auth.getSession();
      const session = sessRes.session;
      if (!session) {
        setState({ loading: false, user: null, session: null, isAdmin: false, error: null });
        return;
      }
      // is_admin() is a SECURITY DEFINER function defined in 0001_init.sql.
      const { data, error } = await supabase.rpc("is_admin");
      if (error) throw error;
      setState({
        loading: false,
        user: session.user,
        session,
        isAdmin: data === true,
        error: null,
      });
    } catch (e) {
      setState({
        loading: false,
        user: null,
        session: null,
        isAdmin: false,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
    const { data: sub } = supabase.auth.onAuthStateChange(() => { void refresh(); });
    return () => sub.subscription.unsubscribe();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    await refresh();
  }, [refresh]);

  return { ...state, signIn, signOut, refresh };
}
